import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { http } from "@/lib/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);     // user object | null
  const [ready, setReady] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const t = localStorage.getItem("rmms_token");
      if (!t) { setUser(null); setReady(true); return; }
      const r = await http.get("/auth/me");
      setUser(r.data);
    } catch (e) {
      console.error("[auth] refresh failed", e);
      localStorage.removeItem("rmms_token");
      setUser(null);
    } finally { setReady(true); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const login = useCallback(async (email, password) => {
    const r = await http.post("/auth/login", { email, password });
    localStorage.setItem("rmms_token", r.data.token);
    setUser(r.data.user);
    return r.data.user;
  }, []);

  const logout = useCallback(async () => {
    try { await http.post("/auth/logout"); } catch (e) { console.warn("[auth] logout request failed", e); }
    localStorage.removeItem("rmms_token");
    setUser(null);
  }, []);

  const value = useMemo(() => ({ user, ready, login, logout, refresh }), [user, ready, login, logout, refresh]);

  return (
    <AuthCtx.Provider value={value}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);

// permission helper — true if the user has the given action on the section
export function can(user, section, action = "read") {
  if (!user) return false;
  if (user.role === "admin") return true;
  const eff = user.effective;
  if (!eff) return false;
  return Boolean(eff.permissions?.[section]?.[action]);
}

export function canJobAction(user, action) {
  if (!user) return false;
  if (user.role === "admin") return true;
  return (user.effective?.job_actions || []).includes(action);
}

export const ROLE_LABEL = {
  admin: "Admin",
  general_manager: "General Manager",
  district_manager: "District Manager",
  area_manager: "Area Manager",
  maintenance_manager: "Maintenance Manager",
  store_manager: "Store Manager",
  store_staff: "Store Staff",
  maintenance_staff: "Maintenance Staff",
  contractor: "Contractor",
};

// Resolves a label, preferring the user's own effective label (for custom roles)
export function roleLabel(slug, user) {
  if (user?.effective?.slug === slug && user.effective?.label) return user.effective.label;
  return ROLE_LABEL[slug] || slug;
}
